/**
 * Clauses tab — single screen that toggles between the Library (CRUD on the
 * tenant's clause bank) and Suggestion (AI-powered matching against the
 * currently selected text). Same underlying data, different mode of use.
 */
import { useState } from 'react';
import { BookOpen, Sparkles } from 'lucide-react';
import { ClauseLibrary } from '../components/ClauseLibrary';
import { ClauseSuggestion } from '../components/ClauseSuggestion';

export type ClausesView = 'library' | 'suggestion';

interface ClausesScreenProps {
  initialView?: ClausesView;
}

export function ClausesScreen({ initialView = 'library' }: ClausesScreenProps) {
  const [view, setView] = useState<ClausesView>(initialView);

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label="Clauses view"
        className="inline-flex items-center bg-[var(--bg-soft-2)] rounded-md p-0.5 self-start"
      >
        <TabButton
          active={view === 'library'}
          onClick={() => setView('library')}
          icon={BookOpen}
          label="Library"
        />
        <TabButton
          active={view === 'suggestion'}
          onClick={() => setView('suggestion')}
          icon={Sparkles}
          label="Suggestion"
        />
      </div>
      <div className="ez-card overflow-hidden">
        {view === 'library' && <ClauseLibrary />}
        {view === 'suggestion' && <ClauseSuggestion />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof BookOpen;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium transition-colors"
      style={{
        background: active ? 'white' : 'transparent',
        color: active ? 'var(--brand-3)' : 'var(--ink-3)',
        boxShadow: active ? 'var(--shadow-1)' : 'none',
      }}
    >
      <Icon size={13} />
      {label}
    </button>
  );
}

export default ClausesScreen;
